import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Box,
  Typography,
  Alert,
  Switch,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { createEmployeeQuick, fetchEmployees } from '../../store/slices/employeeSlice';
import { AppDispatch } from '../../store/store';
import { CreateEmployeeQuick, DropdownItem } from '../../types';
import { masterApi } from '../../api/masterApi';
import { employeeApi } from '../../api/employeeApi';
interface QuickAddEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
}

const MAX_EMPLOYEE_CODE_LENGTH = 10;

const validateEmployeeCode = (employeeCode: string): string => {
  const trimmedCode = employeeCode.trim().toUpperCase();

  if (!trimmedCode) {
    return '';
  }

  if (trimmedCode.length > MAX_EMPLOYEE_CODE_LENGTH) {
    return `Employee code must be at most ${MAX_EMPLOYEE_CODE_LENGTH} characters.`;
  }

  return '';
};

const QuickAddEmployeeModal: React.FC<QuickAddEmployeeModalProps> = ({ open, onClose, onCreated }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skipContactNumber, setSkipContactNumber] = useState(false);
  const [genders, setGenders] = useState<DropdownItem[]>([]);
  const [lastEmployeeCode, setLastEmployeeCode] = useState<string | null>(null);
  const [employeeCodeError, setEmployeeCodeError] = useState('');

  useEffect(() => {
    masterApi.getGenders().then(setGenders).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchLastEmployeeCode = async () => {
      try {
        const response = await employeeApi.getLastEmployeeCode();
        setLastEmployeeCode(response.employeeCode);
      } catch {
        setLastEmployeeCode(null);
      }
    };

    fetchLastEmployeeCode();
  }, [open]);

  const [formData, setFormData] = useState<CreateEmployeeQuick>({
    fullName: '',
    mobileNumber: '',
    employeeCode: '',
    organizationId: undefined,
    gender: '',
    allowMobileCheckin: false,
    restrictLocationCheckin: false,
    allowMultipleCheckin: false,
    allowFaceAttendance: false,
  });

  const handleChange = (field: keyof CreateEmployeeQuick) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;

    if (field === 'employeeCode') {
      const normalizedValue = value.toUpperCase().slice(0, MAX_EMPLOYEE_CODE_LENGTH);
      setFormData({ ...formData, employeeCode: normalizedValue });
      setEmployeeCodeError(validateEmployeeCode(normalizedValue));
      return;
    }

    setFormData({ ...formData, [field]: value });
  };

  const handleSwitchChange = (field: 'allowMobileCheckin' | 'restrictLocationCheckin' | 'allowMultipleCheckin' | 'allowFaceAttendance') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.checked });
  };

  const handleSubmit = async () => {
    setError(null);
    if (!formData.fullName.trim()) {
      setError('Name is required');
      return;
    }

    const employeeCode = formData.employeeCode.trim().toUpperCase();
    const validationError = validateEmployeeCode(employeeCode);
    if (validationError) {
      setEmployeeCodeError(validationError);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        employeeCode,
        mobileNumber: skipContactNumber ? undefined : formData.mobileNumber,
      };
      const result = await dispatch(createEmployeeQuick(payload)).unwrap();
      dispatch(fetchEmployees({}));
      resetForm();
      if (onCreated) {
        onCreated(result.id);
      } else {
        onClose();
      }
    } catch (err: any) {
      setError(err || 'Failed to create employee');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      mobileNumber: '',
      employeeCode: '',
      organizationId: undefined,
      gender: '',
      allowMobileCheckin: false,
      restrictLocationCheckin: false,
      allowMultipleCheckin: false,
      allowFaceAttendance: false,
    });
    setSkipContactNumber(false);
    setError(null);
    setEmployeeCodeError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
<Dialog
  open={open}
  onClose={(_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    handleClose();
  }}
  maxWidth="sm"
  fullWidth
  disableEscapeKeyDown
  PaperProps={{ 'data-app-dialog-skip-top-close': 'true' }}
>
  <DialogTitle>
    <Box>
      <Typography variant="h6" fontWeight="bold">Add Employee</Typography>
      <Typography variant="body2" color="text.secondary">Quick Add - Fill basic details</Typography>
    </Box>
  </DialogTitle>

  <DialogContent dividers>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

    <TextField
      label="Full Name"
      fullWidth
      required
      value={formData.fullName}
      onChange={handleChange('fullName')}
      sx={{ mb: 2 }}
      autoFocus
    />

    <Box sx={{ mb: 2 }}>
      <TextField
        label="Contact Number"
        fullWidth
        value={formData.mobileNumber}
        onChange={e => {
          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
          setFormData({ ...formData, mobileNumber: value });
        }}
        disabled={skipContactNumber}
        placeholder="9876543210"
        inputProps={{ maxLength: 10, pattern: '[0-9]{10}' }}
        helperText={
          formData.mobileNumber && formData.mobileNumber.length !== 10
            ? 'Mobile number must be exactly 10 digits.'
            : ''
        }
        error={!!formData.mobileNumber && formData.mobileNumber.length !== 10}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={skipContactNumber}
            onChange={(e) => setSkipContactNumber(e.target.checked)}
            size="small"
          />
        }
        label={<Typography variant="body2">I will enter the contact number later</Typography>}
      />
    </Box>

    <TextField
      label="Employee Code"
      fullWidth
      value={formData.employeeCode}
      onChange={handleChange('employeeCode')}
      error={!!employeeCodeError}
      helperText={
        employeeCodeError ||
        (lastEmployeeCode
          ? `Last generated code: ${lastEmployeeCode}. Leave blank to generate the next code when saving.`
          : 'No employee code generated yet. Leave blank to generate it when saving.')
      }
      sx={{ mb: 2 }}
      inputProps={{ maxLength: MAX_EMPLOYEE_CODE_LENGTH }}
    />

    <TextField
      label="Gender"
      select
      fullWidth
      value={formData.gender}
      onChange={handleChange('gender')}
      sx={{ mb: 2 }}
    >
      <MenuItem value="">Select Gender</MenuItem>
      {genders.map((g) => (
        <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
      ))}
    </TextField>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
      Employee Settings
    </Typography>
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={formData.allowMobileCheckin}
            onChange={handleSwitchChange('allowMobileCheckin')}
          />
        }
        label="Allow this employee to Checkin/Checkout via Mobile App"
      />
      <FormControlLabel
        control={
          <Switch
            checked={formData.restrictLocationCheckin}
            onChange={handleSwitchChange('restrictLocationCheckin')}
          />
        }
        label="Allow Mobile Checkin Only in Configured Locations"
      />
      <FormControlLabel
        control={
          <Switch
            checked={formData.allowMultipleCheckin}
            onChange={handleSwitchChange('allowMultipleCheckin')}
          />
        }
        label="Allow Multiple Check-in/Check-out Per Day"
      />
    </Box>
  </DialogContent>

  <DialogActions sx={{ px: 3, py: 2 }}>
    <Button onClick={handleClose} disabled={loading} sx={{ textTransform: 'none' }}>
      Cancel
    </Button>
    <Button
      variant="contained"
      onClick={handleSubmit}
      disabled={loading || !formData.fullName.trim() || !!employeeCodeError}
      sx={{ textTransform: 'none' }}
    >
      {loading ? <CircularProgress size={24} /> : 'Save & Continue'}
    </Button>
  </DialogActions>
</Dialog>
  );
};

export default QuickAddEmployeeModal;
